import moment from 'moment';
import Accordion from '../../../../components/Accordion';
import FormInput from '../../../../components/Form/FormInput';
import FormSelectBox from '../../../../components/Form/FormSelectBox';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { useHabilitacionContratista } from '../../../../hooks/useHabilitacionContratista';
import { useUserLogin } from '../../../../hooks/useUserLogin';
import { EmpresaGestoraService } from '../../../../services/EmpresaGestoraService';
import { toast } from 'react-toastify';
import Confirm from '../../../../components/Confirm';
import { useDocumentoHabilitacion } from '../../../../hooks/useDocumentoHabilitacion';
import { usePersonalHabilitacion } from '../../../../hooks/usePersonalHabilitacion';
import { useDocumentoPersonalHabilitacion } from '../../../../hooks/useDocumentoPersonalHabilitacion';

type DatosGeneralesHabilitacionProps = {
  isDisabled: boolean;
  handleCancelar: () => void;
};

const DatosGeneralesHabilitacion = (
  { isDisabled, handleCancelar }: DatosGeneralesHabilitacionProps,
  ref: any,
) => {
  const {
    habilitacionSelected,
    crearHabilitacion,
    updateHabilitacion,
    handleHabilitacionSelected,
  } = useHabilitacionContratista();
  const { getUsuario } = useUserLogin();
  const { documentos, deleteDocumento, buscarDocumentosByIdHabilitacion } = useDocumentoHabilitacion();
  const { personalList } = usePersonalHabilitacion();
  const { buscarDocumentosByPersonal, eliminarDocumento: eliminarDocPersonal } = useDocumentoPersonalHabilitacion();

  const [empresasGestoras, setEmpresasGestoras] = useState<any[]>([]);
  const [form, setForm] = useState<any>({});
  const [showModalCancelar, setShowModalCancelar] = useState(false);
  const [showModalCambioEmpresaGestora, setShowModalCambioEmpresaGestora] = useState(false);
  const [pendingPayload, setPendingPayload] = useState<any>(null);
  const [errorEmpresaGestora, setErrorEmpresaGestora] = useState('');
  const toggleCancelar = () =>
    setShowModalCancelar(!showModalCancelar);
  const toggleCambioEmpresaGestora = () =>
    setShowModalCambioEmpresaGestora(prev => !prev);

  const mapEG = (eg: { id: number; nombreEmpresa: string }) => ({
    id: eg.id,
    descripcion: eg.nombreEmpresa,
  });

  useEffect(() => {
    const idContratista =
      habilitacionSelected.idContratista || getUsuario()?.idContratista;
    if (idContratista) {
      EmpresaGestoraService.findByContratista(idContratista)
        .then((data: any[]) => setEmpresasGestoras(data.map(mapEG)))
        .catch(() => toast.error('Error al cargar empresas gestoras'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [habilitacionSelected.idContratista]);

  useEffect(() => {
    setForm({
      idEmpresaGestora: habilitacionSelected.idEmpresaGestora || '',
      informacionAdicional:
        habilitacionSelected.informacionAdicional || '',
    });
  }, [habilitacionSelected]);

  const handleInputChange = (name: string, value: string) => {
    if (name === 'idEmpresaGestora') setErrorEmpresaGestora('');
    setForm((prev: any) => ({ ...prev, [name]: value }));
  };

  const ejecutarGuardado = (payload: any) => {
    if (payload.id === 0) {
      crearHabilitacion(payload)
        .then((response: any) => {
          if (response && response.data) {
            handleHabilitacionSelected(response.data);
          }
          toast.success('Se creó la habilitación correctamente');
        })
        .catch(() =>
          toast.error('Hubo un error al crear la habilitación'),
        );
    } else {
      updateHabilitacion(payload)
        .then((response: any) => {
          if (response && response.data) {
            handleHabilitacionSelected(response.data);
          }
          toast.success('Se guardó la habilitación correctamente');
        })
        .catch(() =>
          toast.error('Hubo un error al guardar la habilitación'),
        );
    }
  };

  const handleConfirmCambioEmpresaGestora = async () => {
    setShowModalCambioEmpresaGestora(false);

    await Promise.all(documentos.map(doc => deleteDocumento(doc.id.toString())));

    for (const persona of personalList) {
      const response = await buscarDocumentosByPersonal(persona.id);
      if (response?.data?.length) {
        await Promise.all(response.data.map((doc: any) => eliminarDocPersonal(doc.id)));
      }
    }

    if (habilitacionSelected.id !== 0) {
      await buscarDocumentosByIdHabilitacion(habilitacionSelected.id);
    }

    if (pendingPayload) {
      ejecutarGuardado(pendingPayload);
      setPendingPayload(null);
    }
  };

  const handleSubmit = () => {
    const idEmpresaGestora =
      form.idEmpresaGestora || habilitacionSelected.idEmpresaGestora;
    if (!idEmpresaGestora) {
      setErrorEmpresaGestora('Debe seleccionar una Empresa Gestora');
      return;
    }
    const payload = {
      id: habilitacionSelected.id,
      idEmpresaGestora,
      informacionAdicional: form.informacionAdicional,
    };

    const empresaGestoraChanged =
      habilitacionSelected.id !== 0 &&
      String(form.idEmpresaGestora) !== String(habilitacionSelected.idEmpresaGestora);
    const tieneDocumentos = documentos.length > 0 || personalList.length > 0;

    if (empresaGestoraChanged && tieneDocumentos) {
      setPendingPayload(payload);
      setShowModalCambioEmpresaGestora(true);
      return;
    }

    ejecutarGuardado(payload);
  };

  useImperativeHandle(ref, () => ({
    handleSubmit,
  }));

  const cancelar = (e: any) => {
    e.preventDefault();
    setShowModalCancelar(true);
  };

  return (
    <>
      <Confirm
        isOpen={showModalCancelar}
        toggle={toggleCancelar}
        title="Confirmar"
        yesFn={handleCancelar}
      >
        <div className="form-horizontal">
          <div className="row">
            <p>Desea salir sin guardar los cambios?</p>
          </div>
        </div>
      </Confirm>
      <Confirm
        isOpen={showModalCambioEmpresaGestora}
        toggle={toggleCambioEmpresaGestora}
        title="Atención"
        yesFn={handleConfirmCambioEmpresaGestora}
      >
        <div className="form-horizontal">
          <div className="row">
            <p>
              Si cambia la Empresa Gestora se perderá toda la documentación adjuntada
              (documentos de la habilitación y documentos del personal).
              ¿Desea continuar?
            </p>
          </div>
        </div>
      </Confirm>
      <Accordion
        id={'datosGeneralesHabilitacion'}
        title="Datos Generales"
      >
        <div>
          <div className="row mt-3">
            <FormSelectBox
              title={'Empresa Gestora'}
              label={'empresa gestora'}
              id={'empresaGestora'}
              name={'idEmpresaGestora'}
              optionId={'id'}
              optionLabel={'descripcion'}
              value={form.idEmpresaGestora || ''}
              options={empresasGestoras}
              isDisabled={isDisabled}
              errorText={errorEmpresaGestora}
              onChange={(e) =>
                handleInputChange(e.target.name, e.target.value)
              }
            />
            <FormInput
              id={'contratista'}
              label={'Contratista'}
              name={'contratista'}
              value={habilitacionSelected.nombreContratista || ''}
              isDisabled={true}
            />
          </div>
          <div className="row mt-2">
            <FormInput
              id={'estado'}
              label={'Estado'}
              name={'estado'}
              value={habilitacionSelected.nombreEstado || ''}
              isDisabled={true}
            />
            <FormInput
              id={'numeroHabilitacion'}
              label={'Número Habilitación'}
              name={'numeroHabilitacion'}
              value={habilitacionSelected.id ? habilitacionSelected.id.toString() : ''}
              isDisabled={true}
            />
          </div>
          <div className="row mt-2">
            <label
              htmlFor="informacionAdicional"
              className="col-form-label col-2 text-end"
              style={{ color: 'grey' }}
            >
              Información Adicional
            </label>
            <div className="col-10">
              <textarea
                id="informacionAdicional"
                name="informacionAdicional"
                className="form-control"
                rows={3}
                maxLength={500}
                value={form.informacionAdicional || ''}
                disabled={isDisabled}
                onChange={(e) =>
                  handleInputChange(e.target.name, e.target.value)
                }
              />
            </div>
          </div>
          <div className="row mt-2">
            <FormInput
              id={'fechaCreacion'}
              label={'Fecha Creación'}
              name={'fechaCreacion'}
              value={
                habilitacionSelected.fechaCreacion
                  ? moment(habilitacionSelected.fechaCreacion).format(
                      'DD/MM/YYYY',
                    )
                  : ''
              }
              isDisabled={true}
            />
            <FormInput
              id={'usuarioCreacion'}
              label={'Usuario Creación'}
              name={'usuarioCreacion'}
              value={
                habilitacionSelected.apellidoUsuarioCreacion &&
                habilitacionSelected.nombreUsuarioCreacion
                  ? `${habilitacionSelected.apellidoUsuarioCreacion}, ${habilitacionSelected.nombreUsuarioCreacion}`
                  : ''
              }
              isDisabled={true}
            />
          </div>

          <div className="row mt-3"></div>
          {!isDisabled && (
            <div className="text-center">
              <button
                type="button"
                className="btn btn-danger mx-2"
                onClick={cancelar}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-info mx-2"
                onClick={handleSubmit}
              >
                Guardar
              </button>
            </div>
          )}
          <div className="row mt-3"></div>
        </div>
      </Accordion>
    </>
  );
};

export default forwardRef(DatosGeneralesHabilitacion);
